import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  LineChart,
  Line
} from "recharts";
import { 
  Building2, 
  TrendingUp, 
  TrendingDown,
  ArrowLeft,
  Search,
  Filter,
  Download,
  Trophy,
  AlertTriangle,
  X,
  ChevronRight,
  Zap,
  ArrowUpDown,
  CheckSquare,
  Square,
  GitCompare,
  Sparkles,
  Loader2,
  SortAsc,
  SortDesc,
  BarChart3,
  Share2,
  Check,
  Copy,
  Pencil,
  Save,
  RotateCcw,
  FileDown,
  Image,
  Trash2,
  Upload,
  FileSpreadsheet,
  User,
  Phone,
  Mail,
  MapPin,
  Building
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Navigation } from "../components/Navigation";
import { useAuth } from "../contexts/AuthContext";
import { generateMultisitesPDF } from "../utils/pdfExport/index";
import { CsvImportModal } from "../components/CsvImportModal";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const MultisitesReportViewPage = () => {
  const { id: shareToken } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedView, setSelectedView] = useState('overview');
  const [selectedSite, setSelectedSite] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [copied, setCopied] = useState(false);
  
  // Phase 2: Filters
  const [sortBy, setSortBy] = useState('consumption'); // 'consumption', 'name', 'rank'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [consumptionFilter, setConsumptionFilter] = useState('all'); // 'all', 'high', 'medium', 'low'
  const [showAllSites, setShowAllSites] = useState(false); // Show all sites or just 10
  
  // Phase 3: Comparator
  const [selectedForComparison, setSelectedForComparison] = useState([]);
  const [showComparator, setShowComparator] = useState(false);
  
  // Phase 4: AI Analysis
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [isEditingAnalysis, setIsEditingAnalysis] = useState(false);
  const [editedAnalysis, setEditedAnalysis] = useState('');
  const [savingAnalysis, setSavingAnalysis] = useState(false);

  // Logo management
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const logoInputRef = useRef(null);

  // CSV Import management
  const [showCsvImportModal, setShowCsvImportModal] = useState(false);
  const [csvImportType, setCsvImportType] = useState('sites'); // 'sites' or 'monthly'
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null); // Preview data before import
  const [csvFile, setCsvFile] = useState(null); // Store file for confirmation
  const [csvColumnChoice, setCsvColumnChoice] = useState('auto'); // 'auto', 'estimated', 'measured'
  const [csvCorrections, setCsvCorrections] = useState([]); // List of auto-corrected months
  const [csvSourceUnit, setCsvSourceUnit] = useState('kwh'); // 'kwh' or 'mwh' - default to kWh as most exports are in kWh
  const sitesFileInputRef = useRef(null);
  const monthlyFileInputRef = useRef(null);
  
  // Contact info modal state
  const [showContactModal, setShowContactModal] = useState(false);
  
  // Title editing states
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  
  const [contactInfo, setContactInfo] = useState({
    name: '',
    title: '',
    agency: '',
    phone: '',
    email: '',
    address: ''
  });
  const [savingContact, setSavingContact] = useState(false);
  
  // Outlier filtering state
  const [hideOutliers, setHideOutliers] = useState(true);
  
  // Chart segment filter state - for large site counts
  const [chartSegment, setChartSegment] = useState('top20'); // 'top10', 'top20', 'top50', 'bottom10', 'all'
  const [chartPage, setChartPage] = useState(0); // For pagination when showing all sites
  const SITES_PER_PAGE = 25; // Number of sites per page when paginating

  // Handle CSV import for sites
  const handleSitesCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Format invalide', { description: 'Veuillez sélectionner un fichier CSV' });
      return;
    }

    setUploadingCsv(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('source_unit', csvSourceUnit); // Send the source unit

      const response = await axios.post(
        `${API_URL}/api/consumption-reports/${shareToken}/import-sites-csv`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        const unitLabel = csvSourceUnit === 'kwh' ? 'kWh → MWh' : 'MWh';
        toast.success('Import réussi', { 
          description: `${response.data.message} (conversion: ${unitLabel})` 
        });
        // Refresh report data
        const reportResponse = await axios.get(`${API_URL}/api/consumption-reports/${shareToken}`);
        setReport(reportResponse.data.report);
        setShowCsvImportModal(false);
      }
    } catch (error) {
      console.error('CSV import error:', error);
      toast.error('Erreur d\'import', {
        description: error.response?.data?.detail || 'Erreur lors de l\'import du fichier CSV'
      });
    } finally {
      setUploadingCsv(false);
      if (sitesFileInputRef.current) sitesFileInputRef.current.value = '';
    }
  };

  // Handle CSV preview for monthly data (step 1)
  const handleMonthlyCsvPreview = async (e, columnChoice = csvColumnChoice) => {
    const file = e?.target?.files?.[0] || csvFile;
    if (!file) return;

    if (file.name && !file.name.endsWith('.csv')) {
      toast.error('Format invalide', { description: 'Veuillez sélectionner un fichier CSV' });
      return;
    }

    setUploadingCsv(true);
    setCsvFile(file);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('column_choice', columnChoice);

      const response = await axios.post(
        `${API_URL}/api/consumption-reports/${shareToken}/preview-monthly-csv`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setCsvPreview(response.data);
        // Track corrections for alert
        const corrections = response.data.preview.filter(row => row.source?.includes('corrigé'));
        setCsvCorrections(corrections);
      }
    } catch (error) {
      console.error('CSV preview error:', error);
      toast.error('Erreur de prévisualisation', {
        description: error.response?.data?.detail || 'Erreur lors de la lecture du fichier CSV'
      });
      setCsvFile(null);
    } finally {
      setUploadingCsv(false);
      if (monthlyFileInputRef.current) monthlyFileInputRef.current.value = '';
    }
  };

  // Re-preview when column choice changes
  const handleColumnChoiceChange = (choice) => {
    setCsvColumnChoice(choice);
    if (csvFile) {
      handleMonthlyCsvPreview(null, choice);
    }
  };

  // Confirm and import monthly CSV (step 2)
  const handleConfirmMonthlyCsvImport = async () => {
    if (!csvFile) return;

    setUploadingCsv(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('column_choice', csvColumnChoice);

      const response = await axios.post(
        `${API_URL}/api/consumption-reports/${shareToken}/import-monthly-csv`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        toast.success('Import réussi', { description: response.data.message });
        // Refresh report data
        const reportResponse = await axios.get(`${API_URL}/api/consumption-reports/${shareToken}`);
        setReport(reportResponse.data.report);
        setShowCsvImportModal(false);
        setCsvPreview(null);
        setCsvFile(null);
        setCsvCorrections([]);
        setCsvColumnChoice('auto');
      }
    } catch (error) {
      console.error('CSV import error:', error);
      toast.error('Erreur d\'import', {
        description: error.response?.data?.detail || 'Erreur lors de l\'import du fichier CSV'
      });
    } finally {
      setUploadingCsv(false);
    }
  };

  // Cancel preview and reset
  const handleCancelCsvPreview = () => {
    setCsvPreview(null);
    setCsvFile(null);
    setCsvCorrections([]);
    setCsvColumnChoice('auto');
  };

  // Handle logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format invalide', {
        description: 'Veuillez sélectionner une image (PNG, JPG, GIF, SVG, WebP).'
      });
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/api/consumption-reports/${shareToken}/logo`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        setReport(prev => ({ ...prev, client_logo: response.data.logo_url }));
        setLogoError(false);
        toast.success('Logo mis à jour');
      }
    } catch (err) {
      toast.error('Erreur lors de l\'upload du logo');
      console.error(err);
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  // Handle logo delete
  const handleLogoDelete = async () => {
    if (!confirm('Supprimer le logo du rapport ?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/consumption-reports/${shareToken}/logo`,
        { headers: getAuthHeader() }
      );
      setReport(prev => ({ ...prev, client_logo: null }));
      toast.success('Logo supprimé');
    } catch (err) {
      toast.error('Erreur lors de la suppression');
      console.error(err);
    }
  };

  // Save edited title
  const handleSaveTitle = async () => {
    if (!editedTitle.trim()) {
      toast.error('Le titre ne peut pas être vide');
      return;
    }
    setSavingTitle(true);
    try {
      const response = await axios.patch(
        `${API_URL}/api/consumption-reports/${shareToken}`,
        { client_name: editedTitle.trim() },
        { headers: getAuthHeader() }
      );
      if (response.data.success) {
        setReport(prev => ({ ...prev, client_name: editedTitle.trim() }));
        setIsEditingTitle(false);
        toast.success('Titre mis à jour avec succès');
      }
    } catch (err) {
      toast.error('Erreur lors de la mise à jour du titre');
    } finally {
      setSavingTitle(false);
    }
  };


  // Share functionality
  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/consumption/multisites/${shareToken}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Lien copié !', {
        description: 'Le lien de partage a été copié dans le presse-papiers.'
      });
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        toast.success('Lien copié !', {
          description: 'Le lien de partage a été copié dans le presse-papiers.'
        });
      } catch (e) {
        toast.error('Impossible de copier', {
          description: shareUrl
        });
      }
      document.body.removeChild(textArea);
    }
    
    setTimeout(() => setCopied(false), 2000);
  };

  // Record view for non-authenticated users (clients)
  const recordView = async () => {
    if (!user) {
      try {
        await axios.post(`${API_URL}/api/consumption-reports/${shareToken}/view`);
      } catch (err) {
        // Silently fail - view tracking should not affect user experience
        console.log('View not recorded');
      }
    }
  };

  // Export to PDF
  const handleExportPDF = async () => {
    if (!report || !report.sites) {
      toast.error('Données insuffisantes pour générer le PDF');
      return;
    }
    
    try {
      toast.info('Génération du PDF en cours...');
      const fileName = await generateMultisitesPDF(report, report.sites, report.monthly_data);
      toast.success('PDF téléchargé !', {
        description: `Fichier: ${fileName}`
      });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  useEffect(() => {
    fetchReport();
  }, [shareToken]);

  // Record view after report is loaded (only for non-authenticated users)
  useEffect(() => {
    if (report && !user) {
      recordView();
    }
  }, [report, user]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/consumption-reports/${shareToken}`);
      
      if (response.data.report.report_type !== 'consumption_multisites') {
        navigate(`/consumption/report/${shareToken}`);
        return;
      }
      
      setReport(response.data.report);
      
      // Initialize contact info from report
      if (response.data.report.contact_info) {
        setContactInfo(prev => ({
          ...prev,
          ...response.data.report.contact_info
        }));
      }
      
      // Load stored AI analysis if available
      if (response.data.report.energy_manager_analysis) {
        setAiAnalysis(response.data.report.energy_manager_analysis);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors du chargement du rapport");
    } finally {
      setLoading(false);
    }
  };

  // Save contact info
  const handleSaveContactInfo = async () => {
    try {
      setSavingContact(true);
      const response = await axios.put(
        `${API_URL}/api/reports/${shareToken}/contact-info`,
        contactInfo,
        { headers: getAuthHeader() }
      );
      
      if (response.data.success) {
        setReport(prev => ({ ...prev, contact_info: response.data.contact_info }));
        setShowContactModal(false);
        toast.success('Coordonnées enregistrées');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingContact(false);
    }
  };

  // =====================================================
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURN
  // =====================================================
  
  // First check if any site has monthly data (from CSV import)
  const hasAnySiteMonthlyData = useMemo(() => {
    if (!report || !report.sites) return false;
    return report.sites.some(site => 
      (site.current_mwh && site.current_mwh > 0) || 
      (site.previous_mwh && site.previous_mwh > 0)
    );
  }, [report]);
  
  // Calculate outlier threshold using IQR method
  const outlierThreshold = useMemo(() => {
    if (!report || !report.sites || report.sites.length < 4) return Infinity;
    
    const values = report.sites
      .map(s => Math.max(s.current_mwh || 0, s.previous_mwh || 0, s.total_mwh || 0))
      .filter(v => v > 0)
      .sort((a, b) => a - b);
    
    if (values.length < 4) return Infinity;
    
    const q1 = values[Math.floor(values.length * 0.25)];
    const q3 = values[Math.floor(values.length * 0.75)];
    const iqr = q3 - q1;
    // Use 3x IQR for outlier detection (more permissive than standard 1.5x)
    return q3 + (iqr * 3);
  }, [report]);
  
  // Use useMemo to ensure stable data references for Recharts
  const siteChartData = useMemo(() => {
    if (!report || !report.sites) return [];
    
    const processedData = report.sites.map((site, index) => {
      const currentMonthValue = site.current_mwh || 0;
      const previousMonthValue = site.previous_mwh || 0;
      const estimatedValue = site.estimated_mwh || 0;
      const totalYearValue = site.total_mwh || 0;
      
      // Per-site logic: use monthly data if THIS site has it, otherwise use annual total
      const thisSiteHasMonthlyData = currentMonthValue > 0 || previousMonthValue > 0;
      
      // Determine display values for this site
      let mainConsumption, comparisonValue;
      
      if (hasAnySiteMonthlyData) {
        // Report has monthly data from CSV import
        if (thisSiteHasMonthlyData) {
          // This site has monthly data - use it
          mainConsumption = currentMonthValue;
          comparisonValue = previousMonthValue;
        } else {
          // This site doesn't have monthly data - only show estimated if available
          // Don't mix annual totals with monthly comparisons
          mainConsumption = 0;  // Will be hidden by having no value
          comparisonValue = 0;
        }
      } else {
        // No CSV import yet - use annual totals for all sites (legacy mode)
        mainConsumption = totalYearValue;
        comparisonValue = site.previous_year_total_mwh || 0;
      }
      
      // Check if this site is an outlier
      const maxValue = Math.max(mainConsumption, comparisonValue, estimatedValue);
      const isOutlier = maxValue > outlierThreshold;
      
      // Create a truly unique key using index AND site name to prevent collisions
      const uniqueKey = `site_${index}_${site.name.replace(/\s+/g, '_')}`;
      
      return {
        uniqueKey: uniqueKey, // Truly unique identifier
        siteIndex: index, // Numeric index for tracking
        siteName: site.name, // Full original name (keep separate from display name)
        name: site.name.length > 15 ? site.name.substring(0, 15) + '...' : site.name,
        fullName: site.name,
        // Main consumption bar
        consumption: mainConsumption,
        // Comparison bar
        previousMonth: comparisonValue,
        // Estimated for current month (always shown if available)
        estimated: estimatedValue,
        // Keep total for tooltip info
        totalYear: totalYearValue,
        // Track data source for this site
        hasMonthlyData: thisSiteHasMonthlyData,
        // Outlier flag
        isOutlier: isOutlier
      };
    });
    
    // Filter and sort
    return processedData
      .filter(site => {
        if (!hasAnySiteMonthlyData) return true;  // Show all in annual mode
        // In monthly mode, only show sites with at least some data
        return site.consumption > 0 || site.previousMonth > 0 || site.estimated > 0;
      })
      .filter(site => !hideOutliers || !site.isOutlier)
      .sort((a, b) => {
        // Sort by the highest non-zero value for better visualization
        const aMax = Math.max(a.consumption, a.previousMonth, a.estimated);
        const bMax = Math.max(b.consumption, b.previousMonth, b.estimated);
        return bMax - aMax;
      });
  }, [report, hasAnySiteMonthlyData, outlierThreshold, hideOutliers]);
  
  // Apply segment filter to chart data (after sorting)
  const totalSitesInChart = siteChartData.length;
  
  // Use useMemo for segmented data to ensure stable references
  const segmentedChartData = useMemo(() => {
    // Deep clone each item with a new unique displayIndex for the current segment
    const createSegmentData = (sourceData) => {
      return sourceData.map((item, displayIdx) => ({
        ...item,
        // Add display index specific to this segment view
        displayIndex: displayIdx,
        // Create a new object reference to avoid Recharts caching issues
        _chartKey: `${item.uniqueKey}_${displayIdx}_${Date.now()}`
      }));
    };
    
    switch (chartSegment) {
      case 'top10':
        return createSegmentData(siteChartData.slice(0, 10));
      case 'top20':
        return createSegmentData(siteChartData.slice(0, 20));
      case 'top50':
        return createSegmentData(siteChartData.slice(0, 50));
      case 'bottom10':
        // Reverse to get bottom 10 (lowest consumption), then reverse back for display
        return createSegmentData(siteChartData.slice(-10).reverse());
      case 'bottom20':
        return createSegmentData(siteChartData.slice(-20).reverse());
      case 'all':
        // Paginate if showing all
        const start = chartPage * SITES_PER_PAGE;
        const end = start + SITES_PER_PAGE;
        return createSegmentData(siteChartData.slice(start, end));
      default:
        return createSegmentData(siteChartData.slice(0, 20));
    }
  }, [siteChartData, chartSegment, chartPage]);
  
  const totalPagesForChart = Math.ceil(totalSitesInChart / SITES_PER_PAGE);
  
  // Count outliers for display
  const outliersCount = useMemo(() => {
    if (!report || !report.sites) return 0;
    return report.sites.filter(site => {
      const maxValue = Math.max(site.current_mwh || 0, site.previous_mwh || 0, site.total_mwh || 0);
      return maxValue > outlierThreshold;
    }).length;
  }, [report, outlierThreshold]);
  
  // Check if chart is showing monthly or annual data for labels
  const chartShowsMonthlyData = hasAnySiteMonthlyData;
  
  // =====================================================
  // END OF HOOKS - NOW WE CAN HAVE CONDITIONAL RETURNS
  // =====================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation isSharedView={!user} />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation isSharedView={!user} />
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="pt-6">
              <p className="text-destructive">{error || "Rapport non trouvé"}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Month names for parsing and display (moved here for reportMonthNum calculation)
  const monthNamesFr = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const monthNamesLower = ['', 'janvier', 'février', 'mars', 'avril', 'mai', 'juin', 
                           'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  
  // Get monthly data refs for reportMonthNum calculation
  const currentYearMonthlyRef = report.monthly_data?.current_year || {};
  
  // Get report month - PRIORITY ORDER:
  // 1. report.report_month_num (explicit number)
  // 2. Parse from report.report_month string (e.g., "Janvier 2026")
  // 3. Find last month with MEASURED data in current_year (not previous_year)
  let reportMonthNum = parseInt(report.report_month_num || '0');
  
  // If no month num, try to parse from report.report_month string
  if (reportMonthNum === 0 && report.report_month) {
    const monthStr = report.report_month.toLowerCase().split(' ')[0];
    const foundIndex = monthNamesLower.findIndex(m => m && monthStr.startsWith(m.substring(0, 3)));
    if (foundIndex > 0) {
      reportMonthNum = foundIndex;
    }
  }
  
  // Final fallback: find last month with MEASURED data in current_year only
  if (reportMonthNum === 0) {
    const monthsWithMeasuredData = Object.entries(currentYearMonthlyRef)
      .filter(([, monthData]) => (monthData?.measured_mwh || 0) > 0)
      .map(([monthKey]) => parseInt(monthKey))
      .sort((a, b) => a - b);
    
    // If no measured data, try any consumption data
    if (monthsWithMeasuredData.length > 0) {
      reportMonthNum = Math.max(...monthsWithMeasuredData);
    } else {
      const monthsWithAnyData = Object.entries(currentYearMonthlyRef)
        .filter(([, monthData]) => (monthData?.consumption_mwh || 0) > 0)
        .map(([monthKey]) => parseInt(monthKey))
        .sort((a, b) => a - b);
      reportMonthNum = monthsWithAnyData.length > 0 ? Math.max(...monthsWithAnyData) : 1;
    }
  }

  // Build monthly chart data combining current year (estimated & measured), and previous year (N-1)
  const monthlyChartData = (() => {
    const currentYearData = report.monthly_data?.current_year || {};
    const previousYearData = report.monthly_data?.previous_year || {};
    const reportYear = parseInt(report.report_year || '2026');
    
    // Get all month keys (01, 02, ... 12)
    const monthKeys = Object.keys({...currentYearData, ...previousYearData}).sort();
    
    return monthKeys.map(key => {
      const monthNum = parseInt(key);
      const currentData = currentYearData[key] || {};
      const previousData = previousYearData[key] || {};
      
      // Use separate fields - measured_mwh is ONLY shown if explicitly set
      const estimated = currentData.estimated_mwh || 0;
      const measured = currentData.measured_mwh || 0;
      const previous = previousData.consumption_mwh || 0;
      
      // Show measured data if it exists (even for partial months like February)
      // Only hide measured bar if there's NO measured data at all
      const effectiveMeasured = measured > 0 ? measured : 0;
      const effectiveEstimated = estimated > 0 ? estimated : 0;
      
      // A month is "future" only if it has NO data at all (no measured, no estimated)
      const isFutureMonth = measured === 0 && estimated === 0 && monthNum > reportMonthNum;
      
      return {
        month: currentData.name?.substring(0, 3) || previousData.name?.substring(0, 3) || '',
        fullMonth: currentData.name || previousData.name || '',
        estimated: effectiveEstimated,
        measured: effectiveMeasured,
        previous: previous,
        isFuture: isFutureMonth
      };
    });
  })();

  // Calculate metrics
  const totalSites = report.sites?.length || 0;
  const currentMonth = report.totals?.current_month_mwh || 0;
  const previousMonth = report.totals?.previous_month_mwh || 0;
  
  // Calculate evolution using RAW values (1789 vs 1816 = -1.5%)
  const monthDiff = currentMonth - previousMonth;
  const monthDiffPercent = previousMonth > 0 ? ((monthDiff / previousMonth) * 100).toFixed(1) : 0;

  const previousYearTotal = report.totals?.previous_year_mwh || 0;
  
  // Calculate N-1 for the SAME PERIOD (YTD comparison)
  // Use the same monthly data refs as above
  const currentYearMonthly = currentYearMonthlyRef;
  const previousYearMonthly = report.monthly_data?.previous_year || {};
  
  // reportMonthNum, monthNamesFr, monthNamesLower are already defined above
  
  const lastMonthName = monthNamesFr[reportMonthNum] || 'Janvier';
  const isOnlyOneMonth = reportMonthNum <= 1;
  
  // Calculate YTD cumul: sum ALL available data (measured preferred, estimated as fallback)
  // This gives a "cumul à date" that shows everything consumed so far
  let currentYearYTD = 0;
  let lastMonthWithData = reportMonthNum;
  
  for (let m = 1; m <= 12; m++) {
    const monthKey = m.toString().padStart(2, '0');
    const monthData = currentYearMonthly[monthKey] || {};
    const measuredValue = Number(monthData.measured_mwh || 0);
    const estimatedValue = Number(monthData.estimated_mwh || 0);
    
    // Priority: measured > estimated
    // If month has measured data, use it. Otherwise use estimated if available.
    if (measuredValue > 0) {
      currentYearYTD += measuredValue;
      lastMonthWithData = m;
    } else if (estimatedValue > 0 && m <= reportMonthNum) {
      // Use estimated only for months up to the report month (not future months)
      currentYearYTD += estimatedValue;
      lastMonthWithData = m;
    }
  }
  
  // Fallback to totals if no monthly measured data
  if (currentYearYTD === 0) {
    currentYearYTD = report.totals?.current_year_mwh || 0;
  }
  
  // Sum N-1 months for the SAME period (up to lastMonthWithData for fair comparison)
  // Use previous_year data (same as chart uses)
  let previousYearSamePeriod = 0;
  for (let m = 1; m <= lastMonthWithData; m++) {
    const monthKey = m.toString().padStart(2, '0');
    // Use previous_year monthly data (consumption_mwh) - same source as chart
    const previousMonthData = previousYearMonthly[monthKey] || {};
    previousYearSamePeriod += Number(previousMonthData.consumption_mwh || 0);
  }
  
  // If no monthly N-1 data, estimate proportionally (don't use full year)
  let previousYearForComparison = previousYearSamePeriod;
  if (previousYearSamePeriod === 0 && previousYearTotal > 0) {
    // Estimate: (months / 12) * yearlyTotal
    previousYearForComparison = Math.round((lastMonthWithData / 12) * previousYearTotal);
  }
  
  const yearDiff = currentYearYTD - previousYearForComparison;
  const yearDiffPercent = previousYearForComparison > 0 ? ((yearDiff / previousYearForComparison) * 100).toFixed(1) : 0;
  
  // Projected year value
  const projectedYear = report.totals?.projected_year_mwh || 0;

  // Calculate average consumption for ranking
  const avgConsumption = report.sites?.length > 0 
    ? report.sites.reduce((sum, s) => sum + (s.total_mwh || 0), 0) / report.sites.length 
    : 0;

  // Top 5 (lowest consumption = best performers)
  const sortedByConsumption = [...(report.sites || [])].sort((a, b) => (a.total_mwh || 0) - (b.total_mwh || 0));
  const top5 = sortedByConsumption.slice(0, 5);
  
  // Flop 5 (highest consumption = worst performers)
  const flop5 = sortedByConsumption.slice(-5).reverse();

  // Phase 2: Advanced filtering and sorting
  const filteredAndSortedSites = (() => {
    let sites = [...(report.sites || [])];
    
    // Apply search filter
    if (searchTerm) {
      sites = sites.filter(site => 
        site.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply consumption filter
    if (consumptionFilter !== 'all') {
      const maxConsumption = Math.max(...(report.sites || []).map(s => s.total_mwh || 0));
      const thresholdHigh = maxConsumption * 0.6;
      const thresholdLow = maxConsumption * 0.2;
      
      sites = sites.filter(site => {
        const consumption = site.total_mwh || 0;
        if (consumptionFilter === 'high') return consumption >= thresholdHigh;
        if (consumptionFilter === 'medium') return consumption >= thresholdLow && consumption < thresholdHigh;
        if (consumptionFilter === 'low') return consumption < thresholdLow;
        return true;
      });
    }
    
    // Apply sorting
    sites.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'consumption') {
        comparison = (a.total_mwh || 0) - (b.total_mwh || 0);
      } else if (sortBy === 'name') {
        comparison = a.name.localeCompare(b.name);
      }
      return sortOrder === 'desc' ? -comparison : comparison;
    });
    
    return sites;
  })();

  // Phase 3: Toggle site selection for comparison
  const toggleSiteComparison = (site) => {
    setSelectedForComparison(prev => {
      const isSelected = prev.some(s => s.name === site.name);
      if (isSelected) {
        return prev.filter(s => s.name !== site.name);
      } else if (prev.length < 5) {
        return [...prev, site];
      }
      return prev;
    });
  };

  const isSiteSelectedForComparison = (site) => {
    return selectedForComparison.some(s => s.name === site.name);
  };

  // Legacy: for backward compatibility
  const filteredSites = filteredAndSortedSites;

  // Phase 4: AI Analysis function
  const generateAiAnalysis = async () => {
    if (!user) return;
    
    setLoadingAi(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/consumption-reports/${shareToken}/analyze-multisites`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );
      
      if (response.data.success) {
        setAiAnalysis(response.data.analysis);
      }
    } catch (error) {
      console.error('AI analysis error:', error);
      setAiAnalysis("Erreur lors de l'analyse. Veuillez réessayer.");
    } finally {
      setLoadingAi(false);
    }
  };

  // Start editing the analysis
  const startEditingAnalysis = () => {
    setEditedAnalysis(aiAnalysis);
    setIsEditingAnalysis(true);
  };

  // Cancel editing
  const cancelEditingAnalysis = () => {
    setEditedAnalysis('');
    setIsEditingAnalysis(false);
  };

  // Save edited analysis
  const saveEditedAnalysis = async () => {
    if (!user || !editedAnalysis.trim()) return;
    
    setSavingAnalysis(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/consumption-reports/${shareToken}/analysis`,
        { analysis: editedAnalysis },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        setAiAnalysis(editedAnalysis);
        setIsEditingAnalysis(false);
        setEditedAnalysis('');
        toast.success('Analyse sauvegardée', {
          description: 'Vos modifications ont été enregistrées.'
        });
      }
    } catch (error) {
      console.error('Save analysis error:', error);
      toast.error('Erreur lors de la sauvegarde', {
        description: 'Veuillez réessayer.'
      });
    } finally {
      setSavingAnalysis(false);
    }
  };

  // Regenerate AI analysis
  const regenerateAiAnalysis = async () => {
    if (!user) return;
    
    // Confirm before regenerating
    if (aiAnalysis && !window.confirm('Voulez-vous vraiment régénérer l\'analyse ? Les modifications manuelles seront perdues.')) {
      return;
    }
    
    setAiAnalysis(null);
    await generateAiAnalysis();
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation isSharedView={!user} />
      
      {/* Site Detail Modal */}
      <Dialog open={!!selectedSite} onOpenChange={() => setSelectedSite(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              {selectedSite?.name}
            </DialogTitle>
            <DialogDescription>
              Détail de la consommation du site
            </DialogDescription>
          </DialogHeader>
          
          {selectedSite && (
            <div className="space-y-6 pt-4">
              {/* Site KPIs */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Consommation totale</CardDescription>
                    <CardTitle className="text-2xl text-primary">
                      {selectedSite.total_mwh?.toFixed(2)} MWh
                    </CardTitle>
                  </CardHeader>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Part du parc</CardDescription>
                    <CardTitle className="text-2xl">
                      {((selectedSite.total_mwh / (report.sites?.reduce((s, site) => s + (site.total_mwh || 0), 0) || 1)) * 100).toFixed(1)}%
                    </CardTitle>
                  </CardHeader>
                </Card>
              </div>

              {/* Comparison with average */}
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Comparaison avec la moyenne</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span>Ce site</span>
                        <span className="font-medium">{selectedSite.total_mwh?.toFixed(1)} MWh</span>
                      </div>
                      <div className="h-3 bg-primary rounded-full" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span>Moyenne du parc</span>
                        <span className="font-medium">{avgConsumption.toFixed(1)} MWh</span>
                      </div>
                      <div 
                        className="h-3 bg-muted-foreground/30 rounded-full" 
                        style={{ width: `${Math.min((avgConsumption / (selectedSite.total_mwh || 1)) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-4">
                    {selectedSite.total_mwh > avgConsumption 
                      ? `Ce site consomme ${((selectedSite.total_mwh / avgConsumption - 1) * 100).toFixed(0)}% de plus que la moyenne`
                      : `Ce site consomme ${((1 - selectedSite.total_mwh / avgConsumption) * 100).toFixed(0)}% de moins que la moyenne`
                    }
                  </p>
                </CardContent>
              </Card>

              {/* Ranking */}
              <div className="flex items-center justify-between p-4 bg-accent/50 rounded-lg">
                <span className="text-sm">Classement par consommation</span>
                <span className="font-bold text-lg">
                  #{sortedByConsumption.findIndex(s => s.name === selectedSite.name) + 1} / {totalSites}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Hidden file input for logo upload */}
      <input
        type="file"
        ref={logoInputRef}
        onChange={handleLogoUpload}
        accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
        className="hidden"
      />
      
      <div className="container mx-auto px-4 py-4 md:py-8 space-y-4 md:space-y-6">
        {/* Client Logo - Display with edit options for authenticated users */}
        <div className="flex items-center justify-center sm:justify-start gap-3 mb-4">
          {report.client_logo && !logoError ? (
            <div className="relative group">
              <img 
                src={report.client_logo.startsWith('data:') ? report.client_logo : `${API_URL}${report.client_logo}`}
                alt="Logo client" 
                className="h-12 sm:h-16 w-auto max-w-[200px] object-contain"
                data-testid="client-logo"
                onError={() => setLogoError(true)}
              />
              {user && (
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                  >
                    <Image className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleLogoDelete}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
          ) : user && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => logoInputRef.current?.click()}
              disabled={uploadingLogo}
              className="gap-2"
            >
              <Image className="w-4 h-4" />
              {uploadingLogo ? 'Upload...' : logoError ? 'Réuploader le logo' : 'Ajouter un logo'}
            </Button>
          )}
          
          {/* Contact info button */}
          {user && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowContactModal(true)}
              className="gap-2"
              data-testid="contact-info-btn"
            >
              <User className="w-4 h-4" />
              Contact PDF
            </Button>
          )}
        </div>
        
        {/* Header - Responsive */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            {user && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/consumption/reports')}
                className="w-fit"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
            )}
            <div>
              {isEditingTitle ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    className="text-xl font-bold h-10 max-w-lg"
                    data-testid="edit-title-input"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveTitle();
                      if (e.key === 'Escape') setIsEditingTitle(false);
                    }}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={handleSaveTitle}
                    disabled={savingTitle}
                    data-testid="save-title-btn"
                  >
                    {savingTitle ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 text-green-600" />}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setIsEditingTitle(false)}
                    data-testid="cancel-title-btn"
                  >
                    <X className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-bold" data-testid="report-title">
                    {report.client_name || 'Rapport Multisites'}
                  </h1>
                  {user && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        setEditedTitle(report.client_name || '');
                        setIsEditingTitle(true);
                      }}
                      data-testid="edit-title-btn"
                      className="opacity-50 hover:opacity-100"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                {report.building_name} • Période {report.period || '2025'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleShare}
              data-testid="share-report-btn"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2 text-[hsl(var(--emeraude))]" />
                  Copié !
                </>
              ) : (
                <>
                  <Share2 className="w-4 h-4 mr-2" />
                  Partager
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleExportPDF}
              data-testid="export-pdf-btn"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Exporter PDF
            </Button>
          </div>
        </div>

        {/* KPI Cards - Responsive Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {/* Sites Card */}
          <Card>
            <CardHeader className="pb-2 p-4 md:p-6">
              <CardDescription className="text-xs md:text-sm">Sites actifs</CardDescription>
              <CardTitle className="text-2xl md:text-3xl flex items-center gap-2">
                <Building2 className="w-6 h-6 md:w-8 md:h-8 text-primary" />
                {totalSites}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 md:px-6 md:pb-6">
              <p className="text-xs md:text-sm text-muted-foreground">
                Bâtiments suivis dans ce rapport
              </p>
            </CardContent>
          </Card>

          {/* Monthly Card - Shows the report month */}
          <Card>
            <CardHeader className="pb-2 p-4 md:p-6">
              <CardDescription className="text-xs md:text-sm">
                {report.report_month || report.period || 'Mois'}
              </CardDescription>
              <CardTitle className="text-2xl md:text-3xl flex items-center gap-2">
                <Zap className="w-6 h-6 md:w-8 md:h-8 text-primary" />
                {currentMonth.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MWh
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 md:px-6 md:pb-6">
              <div className="flex items-center gap-2">
                {monthDiff < 0 ? (
                  <TrendingDown className="w-4 h-4 text-[hsl(var(--emeraude))]" />
                ) : (
                  <TrendingUp className="w-4 h-4 text-[hsl(var(--ambre))]" />
                )}
                <span className={`text-xs md:text-sm font-medium ${monthDiff < 0 ? 'text-[hsl(var(--emeraude))]' : 'text-[hsl(var(--ambre))]'}`}>
                  {monthDiff > 0 ? '+' : ''}{monthDiffPercent}% vs {report.report_month ? `${report.report_month.split(' ')[0]} ${parseInt(report.report_year || '2026') - 1}` : 'N-1'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {report.report_month ? `${report.report_month.split(' ')[0]} ${parseInt(report.report_year || '2026') - 1}` : 'N-1'} : {previousMonth.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MWh
              </p>
            </CardContent>
          </Card>

          {/* Yearly Card - YTD Cumul à date */}
          <Card className="sm:col-span-2 lg:col-span-1">
            <CardHeader className="pb-2 p-4 md:p-6">
              <CardDescription className="text-xs md:text-sm">
                Cumul à date
              </CardDescription>
              <CardTitle className="text-2xl md:text-3xl flex items-center gap-2">
                <Zap className="w-6 h-6 md:w-8 md:h-8 text-primary" />
                {currentYearYTD.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MWh
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 md:px-6 md:pb-6">
              {previousYearForComparison > 0 && (
                <div className="flex items-center gap-2 mb-1">
                  {yearDiff < 0 ? (
                    <TrendingDown className="w-4 h-4 text-[hsl(var(--emeraude))]" />
                  ) : (
                    <TrendingUp className="w-4 h-4 text-[hsl(var(--ambre))]" />
                  )}
                  <span className={`text-xs md:text-sm font-medium ${yearDiff < 0 ? 'text-[hsl(var(--emeraude))]' : 'text-[hsl(var(--ambre))]'}`}>
                    {yearDiff > 0 ? '+' : ''}{yearDiffPercent}% vs N-1
                  </span>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Fin {monthNamesFr[lastMonthWithData] || lastMonthName} {report.report_year || '2026'}
              </p>
              {previousYearForComparison > 0 && (
                <p className="text-xs text-muted-foreground">
                  Fin {monthNamesFr[lastMonthWithData] || lastMonthName} {parseInt(report.report_year || '2026') - 1} : {previousYearForComparison.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MWh
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bar Chart by Site */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Consommation par site</CardTitle>
                <CardDescription>
                  {chartShowsMonthlyData 
                    ? `Comparaison ${report.report_month || 'mensuelle'} ${report.report_year || ''} vs ${parseInt(report.report_year || '2026') - 1}`
                    : `Consommation totale par site - ${report.report_year || ''}`
                  }
                  {!chartShowsMonthlyData && (
                    <span className="ml-2 text-amber-600">(Importez un CSV pour voir la comparaison mensuelle)</span>
                  )}
                </CardDescription>
                
                {/* Segment filter buttons - visible when more than 15 sites */}
                {totalSitesInChart > 15 && (
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <span className="text-xs text-muted-foreground mr-1">Afficher :</span>
                    <div className="flex flex-wrap gap-1">
                      <button
                        onClick={() => { setChartSegment('top10'); setChartPage(0); }}
                        className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                          chartSegment === 'top10'
                            ? 'bg-[hsl(var(--ambre)/0.15)] border-[hsl(var(--ambre)/0.5)] text-[hsl(var(--ambre))]'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                        data-testid="chart-filter-top10"
                      >
                        Top 10 🔥
                      </button>
                      <button
                        onClick={() => { setChartSegment('top20'); setChartPage(0); }}
                        className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                          chartSegment === 'top20'
                            ? 'bg-[hsl(var(--ambre)/0.1)] border-[hsl(var(--ambre)/0.4)] text-[hsl(var(--ambre))]'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                        data-testid="chart-filter-top20"
                      >
                        Top 20
                      </button>
                      {totalSitesInChart > 50 && (
                        <button
                          onClick={() => { setChartSegment('top50'); setChartPage(0); }}
                          className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                            chartSegment === 'top50'
                              ? 'bg-[hsl(var(--bleu-petrole)/0.15)] border-[hsl(var(--bleu-petrole)/0.4)] text-[hsl(var(--bleu-petrole))]'
                              : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                          data-testid="chart-filter-top50"
                        >
                          Top 50
                        </button>
                      )}
                      <span className="text-gray-300 mx-1">|</span>
                      <button
                        onClick={() => { setChartSegment('bottom10'); setChartPage(0); }}
                        className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                          chartSegment === 'bottom10'
                            ? 'bg-[hsl(var(--emeraude)/0.15)] border-[hsl(var(--emeraude)/0.5)] text-[hsl(var(--emeraude))]'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                        data-testid="chart-filter-bottom10"
                      >
                        10 économes 🌱
                      </button>
                      <button
                        onClick={() => { setChartSegment('bottom20'); setChartPage(0); }}
                        className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                          chartSegment === 'bottom20'
                            ? 'bg-[hsl(var(--emeraude)/0.1)] border-[hsl(var(--emeraude)/0.4)] text-[hsl(var(--emeraude))]'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                        data-testid="chart-filter-bottom20"
                      >
                        20 économes
                      </button>
                      <span className="text-gray-300 mx-1">|</span>
                      <button
                        onClick={() => { setChartSegment('all'); setChartPage(0); }}
                        className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                          chartSegment === 'all'
                            ? 'bg-blue-50 border-blue-300 text-blue-700'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                        data-testid="chart-filter-all"
                      >
                        Tous ({totalSitesInChart})
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Outliers indicator and toggle */}
                {outliersCount > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => setHideOutliers(!hideOutliers)}
                      className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                        hideOutliers 
                          ? 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100' 
                          : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {hideOutliers 
                        ? `${outliersCount} site(s) à forte conso masqué(s)` 
                        : `Affichage de tous les sites`
                      }
                    </button>
                    <span className="text-xs text-muted-foreground">
                      (cliquez pour {hideOutliers ? 'afficher' : 'masquer'})
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {user && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCsvImportType('sites');
                      setShowCsvImportModal(true);
                    }}
                    className="gap-2"
                    data-testid="import-sites-csv-btn"
                  >
                    <Upload className="w-4 h-4" />
                    <span className="hidden sm:inline">Importer CSV</span>
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-2 md:p-6">
            {/* Pagination controls for "all" segment */}
            {chartSegment === 'all' && totalPagesForChart > 1 && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <button
                  onClick={() => setChartPage(Math.max(0, chartPage - 1))}
                  disabled={chartPage === 0}
                  className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  ← Précédent
                </button>
                <span className="text-sm text-muted-foreground">
                  Page {chartPage + 1} / {totalPagesForChart} 
                  <span className="ml-2 text-xs">
                    (sites {chartPage * SITES_PER_PAGE + 1} - {Math.min((chartPage + 1) * SITES_PER_PAGE, totalSitesInChart)})
                  </span>
                </span>
                <button
                  onClick={() => setChartPage(Math.min(totalPagesForChart - 1, chartPage + 1))}
                  disabled={chartPage >= totalPagesForChart - 1}
                  className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Suivant →
                </button>
              </div>
            )}
            
            {/* Chart info badge */}
            <div className="flex justify-center mb-2">
              <span className="text-xs text-muted-foreground bg-gray-50 px-2 py-1 rounded-full">
                {chartSegment === 'top10' && '🔥 10 sites les plus énergivores'}
                {chartSegment === 'top20' && '⚡ 20 sites les plus énergivores'}
                {chartSegment === 'top50' && '📊 50 sites les plus énergivores'}
                {chartSegment === 'bottom10' && '🌱 10 sites les plus économes'}
                {chartSegment === 'bottom20' && '🍃 20 sites les plus économes'}
                {chartSegment === 'all' && `📋 Tous les sites (${segmentedChartData.length} affichés)`}
              </span>
            </div>
            
            <div className="h-64 md:h-96 overflow-x-auto">
              <div className="min-w-[600px] h-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={segmentedChartData}
                    margin={{ top: 5, right: 20, left: 10, bottom: 80 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="uniqueKey"
                      tickFormatter={(value, index) => {
                        // Find the actual data item to get the display name
                        const item = segmentedChartData[index];
                        return item ? item.name : value;
                      }}
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      tick={{ fontSize: 10 }}
                      stroke="#6b7280"
                    />
                    <YAxis 
                      label={{ value: 'MWh', angle: -90, position: 'insideLeft', style: { fill: '#6b7280', fontSize: 11 } }}
                      tick={{ fontSize: 11 }}
                      stroke="#6b7280"
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        padding: '12px'
                      }}
                      content={({ active, payload }) => {
                        if (!active || !payload || payload.length === 0) return null;
                        
                        // payload[0].payload contains the full data object for the hovered bar
                        const barData = payload[0]?.payload;
                        if (!barData) return null;
                        
                        // Use siteName (original full name) for display
                        const displayName = barData.siteName || barData.fullName || barData.name || 'Site inconnu';
                        
                        return (
                          <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg max-w-sm">
                            <p className="font-semibold text-sm mb-2 break-words">{displayName}</p>
                            {(barData.consumption || 0) > 0 && (
                              <p className="text-sm text-blue-600">
                                {chartShowsMonthlyData ? 'Mesuré' : 'Total'}: {(barData.consumption || 0).toFixed(2)} MWh
                              </p>
                            )}
                            {(barData.previousMonth || 0) > 0 && (
                              <p className="text-sm text-gray-500">N-1: {(barData.previousMonth || 0).toFixed(2)} MWh</p>
                            )}
                            {(barData.estimated || 0) > 0 && (
                              <p className="text-sm text-amber-600">Estimé: {(barData.estimated || 0).toFixed(2)} MWh</p>
                            )}
                            {(barData.totalYear || 0) > 0 && (barData.totalYear || 0) !== (barData.consumption || 0) && chartShowsMonthlyData && (
                              <p className="text-xs text-gray-400 mt-1 border-t pt-1">Cumul annuel: {(barData.totalYear || 0).toFixed(2)} MWh</p>
                            )}
                          </div>
                        );
                      }}
                    />
                    <Legend wrapperStyle={{ paddingTop: '10px', fontSize: '12px' }} />
                    <Bar 
                      dataKey="consumption" 
                      fill="#397DFF"
                      radius={[4, 4, 0, 0]}
                      name={chartShowsMonthlyData 
                        ? `Mesuré ${report.report_month?.split(' ')[0] || ''} ${report.report_year || ''}`
                        : `Total ${report.report_year || ''}`
                      }
                    />
                    <Bar 
                      dataKey="previousMonth" 
                      fill="#687175"
                      radius={[4, 4, 0, 0]}
                      name={chartShowsMonthlyData 
                        ? `${report.report_month?.split(' ')[0] || 'Même mois'} ${parseInt(report.report_year || '2026') - 1}`
                        : `Total ${parseInt(report.report_year || '2026') - 1}`
                      }
                    />
                    <Bar 
                      dataKey="estimated" 
                      fill="#F9C80E"
                      radius={[4, 4, 0, 0]}
                      name="Estimé"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Aggregated Chart */}
        <Card>
          <CardHeader className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base md:text-lg">Évolution mensuelle tous sites confondus</CardTitle>
                <CardDescription className="text-xs md:text-sm">
                  Estimé (jaune) vs Mesuré (bleu) vs N-1 (gris) - {report.report_year || '2026'}
                </CardDescription>
              </div>
              {user && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCsvImportType('monthly');
                    setShowCsvImportModal(true);
                  }}
                  className="gap-2"
                  data-testid="import-monthly-csv-btn"
                >
                  <Upload className="w-4 h-4" />
                  <span className="hidden sm:inline">Importer CSV</span>
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-2 md:p-6">
            <div className="h-64 md:h-96 overflow-x-auto">
              <div className="min-w-[500px] h-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={monthlyChartData}
                    margin={{ top: 5, right: 20, left: 10, bottom: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12 }}
                    stroke="#6b7280"
                  />
                  <YAxis 
                    label={{ value: 'MWh', angle: -90, position: 'insideLeft', style: { fill: '#6b7280' } }}
                    tick={{ fontSize: 12 }}
                    stroke="#6b7280"
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px'
                    }}
                    formatter={(value, name) => [`${value.toFixed(1)} MWh`, name]}
                  />
                  <Legend />
                  <Bar 
                    dataKey="estimated" 
                    fill="#F9C80E"
                    radius={[4, 4, 0, 0]}
                    name={`Estimé ${report.report_year || '2026'}`}
                  />
                  <Bar 
                    dataKey="measured" 
                    fill="#397DFF"
                    radius={[4, 4, 0, 0]}
                    name={`Mesuré ${report.report_year || '2026'}`}
                  />
                  <Bar 
                    dataKey="previous" 
                    fill="#687175"
                    radius={[4, 4, 0, 0]}
                    name={`${parseInt(report.report_year || '2026') - 1}`}
                  />
                </BarChart>
              </ResponsiveContainer>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top/Flop Analysis Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          {/* Top 5 - Best Performers (lowest consumption) - Corporate Émeraude */}
          <Card className="border-[hsl(var(--emeraude)/0.3)] dark:border-[hsl(var(--emeraude)/0.2)]">
            <CardHeader className="pb-3 p-4 md:p-6">
              <div className="flex items-center gap-2">
                <div className="p-1.5 md:p-2 bg-[hsl(var(--emeraude)/0.1)] dark:bg-[hsl(var(--emeraude)/0.15)] rounded-lg">
                  <Trophy className="w-4 h-4 md:w-5 md:h-5 text-[hsl(var(--emeraude))] dark:text-[hsl(var(--emeraude))]" />
                </div>
                <div>
                  <CardTitle className="text-base md:text-lg">Top 5 - Économes</CardTitle>
                  <CardDescription className="text-xs md:text-sm">Sites les plus économes en énergie</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 md:px-6 md:pb-6">
              <div className="space-y-1.5 md:space-y-2">
                {top5.map((site, index) => (
                  <div 
                    key={index}
                    onClick={() => setSelectedSite(site)}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-[hsl(var(--emeraude)/0.05)] dark:hover:bg-[hsl(var(--emeraude)/0.1)] cursor-pointer transition-colors group"
                  >
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className={`w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center text-xs md:text-sm font-bold
                        ${index === 0 ? 'bg-[hsl(var(--emeraude))] text-white' : 
                          index === 1 ? 'bg-[hsl(var(--emeraude)/0.7)] text-white' : 
                          index === 2 ? 'bg-[hsl(var(--emeraude)/0.5)] text-white' : 
                          'bg-[hsl(var(--emeraude)/0.15)] text-[hsl(var(--emeraude))]'}`}>
                        {index + 1}
                      </div>
                      <span className="font-medium text-xs md:text-sm truncate max-w-[100px] md:max-w-[150px]">{site.name}</span>
                    </div>
                    <div className="flex items-center gap-1 md:gap-2">
                      <span className="text-xs md:text-sm font-semibold text-[hsl(var(--emeraude))] dark:text-[hsl(var(--emeraude))]">
                        {site.total_mwh?.toFixed(1)} MWh
                      </span>
                      <ChevronRight className="w-3 h-3 md:w-4 md:h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Flop 5 - Highest Consumption - Corporate Ambre */}
          <Card className="border-[hsl(var(--ambre)/0.3)] dark:border-[hsl(var(--ambre)/0.2)]">
            <CardHeader className="pb-3 p-4 md:p-6">
              <div className="flex items-center gap-2">
                <div className="p-1.5 md:p-2 bg-[hsl(var(--ambre)/0.1)] dark:bg-[hsl(var(--ambre)/0.15)] rounded-lg">
                  <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 text-[hsl(var(--ambre))] dark:text-[hsl(var(--ambre))]" />
                </div>
                <div>
                  <CardTitle className="text-base md:text-lg">Top 5 - Énergivores</CardTitle>
                  <CardDescription className="text-xs md:text-sm">Sites à auditer en priorité</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 md:px-6 md:pb-6">
              <div className="space-y-1.5 md:space-y-2">
                {flop5.map((site, index) => (
                  <div 
                    key={index}
                    onClick={() => setSelectedSite(site)}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-[hsl(var(--ambre)/0.05)] dark:hover:bg-[hsl(var(--ambre)/0.1)] cursor-pointer transition-colors group"
                  >
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className={`w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center text-xs md:text-sm font-bold
                        ${index === 0 ? 'bg-[hsl(var(--ambre))] text-[hsl(var(--secondary))]' : 
                          index === 1 ? 'bg-[hsl(var(--ambre)/0.7)] text-[hsl(var(--secondary))]' : 
                          index === 2 ? 'bg-[hsl(var(--ambre)/0.5)] text-[hsl(var(--secondary))]' : 
                          'bg-[hsl(var(--ambre)/0.15)] text-[hsl(var(--ambre))]'}`}>
                        {index + 1}
                      </div>
                      <span className="font-medium text-xs md:text-sm truncate max-w-[100px] md:max-w-[150px]">{site.name}</span>
                    </div>
                    <div className="flex items-center gap-1 md:gap-2">
                      <span className="text-xs md:text-sm font-semibold text-[hsl(var(--ambre))] dark:text-[hsl(var(--ambre))]">
                        {site.total_mwh?.toFixed(1)} MWh
                      </span>
                      <ChevronRight className="w-3 h-3 md:w-4 md:h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sites Preview List with Filters */}
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Liste des sites</CardTitle>
                  <CardDescription>
                    {filteredAndSortedSites.length} sites affichés sur {totalSites}
                    {selectedForComparison.length > 0 && (
                      <span className="ml-2 text-primary">• {selectedForComparison.length} sélectionné(s)</span>
                    )}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {selectedForComparison.length >= 2 && (
                    <Button 
                      onClick={() => setShowComparator(true)}
                      className="bg-primary"
                      size="sm"
                    >
                      <GitCompare className="w-4 h-4 mr-2" />
                      Comparer ({selectedForComparison.length})
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Filters Row */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input 
                    type="text"
                    placeholder="Rechercher un site..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 pr-4 py-2 text-sm border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                
                {/* Sort By */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[160px]">
                    <ArrowUpDown className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Trier par" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="consumption">Consommation</SelectItem>
                    <SelectItem value="name">Nom</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Sort Order */}
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                  title={sortOrder === 'asc' ? 'Croissant' : 'Décroissant'}
                >
                  {sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
                </Button>
                
                {/* Consumption Filter */}
                <Select value={consumptionFilter} onValueChange={setConsumptionFilter}>
                  <SelectTrigger className="w-[160px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filtrer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les sites</SelectItem>
                    <SelectItem value="high">Haute conso</SelectItem>
                    <SelectItem value="medium">Moyenne conso</SelectItem>
                    <SelectItem value="low">Basse conso</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Clear selection */}
                {selectedForComparison.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setSelectedForComparison([])}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Effacer sélection
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(showAllSites ? filteredSites : filteredSites.slice(0, 10)).map((site, index) => {
                const rank = sortedByConsumption.findIndex(s => s.name === site.name) + 1;
                const isTop = rank <= 3;
                const isFlop = rank > totalSites - 3;
                const isSelectedForComp = isSiteSelectedForComparison(site);
                
                return (
                  <div 
                    key={index}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors group
                      ${isSelectedForComp ? 'bg-primary/10 border-primary' : 'hover:bg-accent/50'}`}
                    data-testid={`site-item-${index}`}
                  >
                    {/* Checkbox for comparison */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSiteComparison(site);
                      }}
                      className="mr-2 p-1 hover:bg-accent rounded"
                      title={isSelectedForComp ? "Retirer de la comparaison" : "Ajouter à la comparaison"}
                    >
                      {isSelectedForComp ? (
                        <CheckSquare className="w-5 h-5 text-primary" />
                      ) : (
                        <Square className="w-5 h-5 text-muted-foreground" />
                      )}
                    </button>
                    
                    <div 
                      className="flex items-center gap-3 flex-1 cursor-pointer"
                      onClick={() => setSelectedSite(site)}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold
                        ${isTop ? 'bg-[hsl(var(--emeraude)/0.15)] text-[hsl(var(--emeraude))]' : 
                          isFlop ? 'bg-[hsl(var(--ambre)/0.15)] text-[hsl(var(--ambre))]' : 
                          'bg-primary/10 text-primary'}`}>
                        {rank}
                      </div>
                      <div>
                        <p className="font-medium">{site.name}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Zap className="w-3 h-3" />
                          {((site.total_mwh / (report.sites?.reduce((s, st) => s + (st.total_mwh || 0), 0) || 1)) * 100).toFixed(1)}% du parc
                        </p>
                      </div>
                    </div>
                    <div 
                      className="flex items-center gap-3 cursor-pointer"
                      onClick={() => setSelectedSite(site)}
                    >
                      <div className="text-right">
                        <p className="font-semibold">{site.total_mwh?.toFixed(2)} MWh</p>
                        <p className="text-xs text-muted-foreground">Total</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                );
              })}
              
              {filteredSites.length === 0 && searchTerm && (
                <p className="text-center text-muted-foreground py-4">
                  Aucun site trouvé pour "{searchTerm}"
                </p>
              )}
              
              {filteredSites.length > 10 && (
                <Button 
                  variant="ghost" 
                  className="w-full mt-4"
                  onClick={() => setShowAllSites(!showAllSites)}
                >
                  {showAllSites ? 'Afficher moins' : `Voir tous les ${filteredSites.length} sites`}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Phase 4: Analyse de nos Energy Managers */}
        <Card className="border-[#397DFF]/30 bg-gradient-to-br from-white to-[#F5F6F9] dark:from-slate-900 dark:to-slate-800">
          <CardHeader className="border-b border-[#397DFF]/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-[#397DFF] rounded-xl shadow-lg shadow-[#397DFF]/20">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl font-semibold text-[#191D1E] dark:text-white">
                    Analyse de nos Energy Managers
                  </CardTitle>
                  <CardDescription className="text-[#687175]">
                    Analyse intelligente de votre parc immobilier par Place des Énergies
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!aiAnalysis && !loadingAi && user && (
                  <Button 
                    onClick={generateAiAnalysis}
                    className="bg-[#397DFF] hover:bg-[#2a6ae8] text-white shadow-lg shadow-[#397DFF]/30 transition-all hover:shadow-xl"
                    data-testid="analyze-park-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyser le parc
                  </Button>
                )}
                {aiAnalysis && user && !isEditingAnalysis && (
                  <>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={startEditingAnalysis}
                      className="border-[#397DFF]/30 text-[#397DFF] hover:bg-[#397DFF]/10"
                      data-testid="edit-analysis-btn"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Modifier
                    </Button>
                    <Button 
                      variant="ghost"
                      size="sm"
                      onClick={regenerateAiAnalysis}
                      className="text-[#687175] hover:text-[#397DFF]"
                      title="Régénérer l'analyse"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {!user && !aiAnalysis && (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F5F6F9] rounded-full mb-4">
                  <Sparkles className="w-8 h-8 text-[#939BA0]" />
                </div>
                <p className="text-[#687175]">
                  Connectez-vous pour accéder à l'analyse de nos Energy Managers
                </p>
              </div>
            )}
            {loadingAi && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-[#397DFF]/20 rounded-full"></div>
                  <div className="absolute top-0 left-0 w-16 h-16 border-4 border-[#397DFF] rounded-full border-t-transparent animate-spin"></div>
                </div>
                <span className="mt-4 text-[#687175] font-medium">Analyse en cours par nos experts...</span>
              </div>
            )}
            {aiAnalysis && !isEditingAnalysis && (
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-inner border border-[#BFC6CC]/30">
                <div className="prose prose-sm max-w-none dark:prose-invert
                  prose-headings:text-[#191D1E] dark:prose-headings:text-white
                  prose-headings:font-semibold prose-headings:border-b prose-headings:border-[#397DFF]/20 prose-headings:pb-2 prose-headings:mb-3
                  prose-strong:text-[#397DFF] prose-strong:font-semibold
                  prose-li:text-[#3C4649] dark:prose-li:text-gray-300 prose-li:marker:text-[#397DFF]
                  prose-p:text-[#3C4649] dark:prose-p:text-gray-300 prose-p:leading-relaxed">
                  <div className="text-sm leading-relaxed space-y-2">
                    {aiAnalysis.split('\n').map((line, index) => {
                      const trimmedLine = line.trim();
                      
                      // Main title (## TITRE)
                      if (trimmedLine.startsWith('## ')) {
                        const headerText = trimmedLine.replace(/^##\s*/, '').replace(/\*\*/g, '');
                        return (
                          <h2 key={index} className="text-xl font-bold text-[#191D1E] dark:text-white border-b-2 border-[#397DFF] pb-3 mb-4 mt-2 first:mt-0">
                            {headerText}
                          </h2>
                        );
                      }
                      
                      // Section title (### 1. TITRE)
                      if (trimmedLine.startsWith('### ')) {
                        const headerText = trimmedLine.replace(/^###\s*/, '').replace(/\*\*/g, '');
                        const headerNum = headerText.match(/^\d+\./);
                        return (
                          <h3 key={index} className="flex items-center gap-3 text-base font-semibold text-[#191D1E] dark:text-white mt-6 mb-3 first:mt-0">
                            {headerNum && (
                              <span className="inline-flex items-center justify-center w-8 h-8 bg-[#397DFF] text-white text-sm font-bold rounded-lg shadow-sm">
                                {headerNum[0].replace('.', '')}
                              </span>
                            )}
                            <span>{headerText.replace(/^\d+\.\s*/, '')}</span>
                          </h3>
                        );
                      }
                      
                      // Subsection title (TITRE EN MAJUSCULES :)
                      if (trimmedLine.match(/^[A-ZÀÂÄÉÈÊËÏÎÔÙÛÜ\s]+\s*:$/)) {
                        return (
                          <h4 key={index} className="text-sm font-semibold text-[#3C4649] dark:text-gray-200 mt-4 mb-2 uppercase tracking-wide">
                            {trimmedLine.replace(':', '')}
                          </h4>
                        );
                      }
                      
                      // Bold inline text with ** **
                      if (trimmedLine.includes('**') && !trimmedLine.startsWith('-') && !trimmedLine.startsWith('•')) {
                        const parts = line.split(/(\*\*[^*]+\*\*)/g);
                        return (
                          <p key={index} className="my-2 text-[#3C4649] dark:text-gray-300">
                            {parts.map((part, i) => {
                              if (part.startsWith('**') && part.endsWith('**')) {
                                return <strong key={i} className="text-[#397DFF] font-semibold">{part.slice(2, -2)}</strong>;
                              }
                              return part;
                            })}
                          </p>
                        );
                      }
                      
                      // Bullet points (- or •)
                      if (trimmedLine.startsWith('-') || trimmedLine.startsWith('•')) {
                        const bulletContent = trimmedLine.replace(/^[-•]\s*/, '');
                        const parts = bulletContent.split(/(\*\*[^*]+\*\*)/g);
                        return (
                          <div key={index} className="flex items-start gap-3 ml-4 my-1.5">
                            <span className="w-2 h-2 bg-[#397DFF] rounded-full mt-1.5 flex-shrink-0"></span>
                            <span className="text-[#3C4649] dark:text-gray-300">
                              {parts.map((part, i) => {
                                if (part.startsWith('**') && part.endsWith('**')) {
                                  return <strong key={i} className="text-[#397DFF] font-semibold">{part.slice(2, -2)}</strong>;
                                }
                                return part;
                              })}
                            </span>
                          </div>
                        );
                      }
                      
                      // Priority/Important line
                      if (trimmedLine.toUpperCase().includes('PRIORITÉ') || trimmedLine.toUpperCase().includes('URGENCE')) {
                        return (
                          <div key={index} className="bg-[#F9C80E]/10 border-l-4 border-[#F9C80E] p-3 rounded-r-lg my-3">
                            <p className="text-[#3C4649] dark:text-gray-200 font-medium">
                              {trimmedLine.replace(/\*\*/g, '')}
                            </p>
                          </div>
                        );
                      }
                      
                      // Regular text
                      if (trimmedLine) {
                        return <p key={index} className="my-2 text-[#3C4649] dark:text-gray-300">{trimmedLine.replace(/\*\*/g, '')}</p>;
                      }
                      return null;
                    })}
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-[#BFC6CC]/30 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-[#939BA0]">
                    <Sparkles className="w-3 h-3" />
                    <span>Analyse générée par nos Energy Managers</span>
                  </div>
                  <Badge variant="outline" className="text-[#10D183] border-[#10D183]/30 bg-[#10D183]/10">
                    Place des Énergies
                  </Badge>
                </div>
              </div>
            )}
            
            {/* Edit Mode */}
            {isEditingAnalysis && (
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-inner border border-[#397DFF]/30">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Pencil className="w-5 h-5 text-[#397DFF]" />
                    <h3 className="font-semibold text-[#191D1E] dark:text-white">Mode édition</h3>
                  </div>
                  <p className="text-xs text-[#939BA0]">
                    Utilisez le format Markdown : ## Titre, ### Section, **gras**, - liste
                  </p>
                </div>
                <textarea
                  value={editedAnalysis}
                  onChange={(e) => setEditedAnalysis(e.target.value)}
                  className="w-full h-96 p-4 border border-[#BFC6CC]/50 rounded-lg text-sm font-mono
                    bg-[#F5F6F9] dark:bg-slate-900 text-[#3C4649] dark:text-gray-300
                    focus:outline-none focus:ring-2 focus:ring-[#397DFF]/30 focus:border-[#397DFF]
                    resize-y"
                  placeholder="Modifiez l'analyse ici..."
                  data-testid="analysis-textarea"
                />
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-xs text-[#939BA0]">
                    {editedAnalysis.length} caractères
                  </p>
                  <div className="flex items-center gap-3">
                    <Button 
                      variant="outline"
                      onClick={cancelEditingAnalysis}
                      disabled={savingAnalysis}
                      className="border-[#BFC6CC] text-[#687175]"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Annuler
                    </Button>
                    <Button 
                      onClick={saveEditedAnalysis}
                      disabled={savingAnalysis || !editedAnalysis.trim()}
                      className="bg-[#10D183] hover:bg-[#0eb874] text-white"
                      data-testid="save-analysis-btn"
                    >
                      {savingAnalysis ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sauvegarde...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Sauvegarder
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {!aiAnalysis && !loadingAi && user && (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#397DFF]/10 to-[#10D183]/10 rounded-2xl mb-4">
                  <BarChart3 className="w-10 h-10 text-[#397DFF]" />
                </div>
                <p className="text-[#687175] mb-2">
                  Obtenez une analyse détaillée de votre parc immobilier
                </p>
                <p className="text-sm text-[#939BA0]">
                  Nos Energy Managers analysent vos données pour vous fournir des recommandations personnalisées
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Phase 3: Comparator Modal */}
      <Dialog open={showComparator} onOpenChange={setShowComparator}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="w-5 h-5 text-primary" />
              Comparaison de {selectedForComparison.length} sites
            </DialogTitle>
            <DialogDescription>
              Analyse comparative côte à côte des sites sélectionnés
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 pt-4">
            {/* Comparison Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-medium text-muted-foreground">Métrique</th>
                    {selectedForComparison.map((site, idx) => (
                      <th key={idx} className="text-center p-3 font-medium min-w-[150px]">
                        <div className="flex flex-col items-center">
                          <span className="truncate max-w-[140px]">{site.name}</span>
                          <Badge variant="outline" className="mt-1">Site {idx + 1}</Badge>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b hover:bg-accent/50">
                    <td className="p-3 font-medium">Consommation totale</td>
                    {selectedForComparison.map((site, idx) => (
                      <td key={idx} className="text-center p-3 font-semibold">
                        {site.total_mwh?.toFixed(2)} MWh
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b hover:bg-accent/50">
                    <td className="p-3 font-medium">Part du parc</td>
                    {selectedForComparison.map((site, idx) => {
                      const totalParc = report.sites?.reduce((s, st) => s + (st.total_mwh || 0), 0) || 1;
                      const percentage = ((site.total_mwh || 0) / totalParc * 100).toFixed(1);
                      return (
                        <td key={idx} className="text-center p-3">
                          {percentage}%
                        </td>
                      );
                    })}
                  </tr>
                  <tr className="border-b hover:bg-accent/50">
                    <td className="p-3 font-medium">Classement</td>
                    {selectedForComparison.map((site, idx) => {
                      const rank = sortedByConsumption.findIndex(s => s.name === site.name) + 1;
                      return (
                        <td key={idx} className="text-center p-3">
                          <Badge variant={rank <= 3 ? "default" : rank > totalSites - 3 ? "destructive" : "secondary"}>
                            #{rank} / {totalSites}
                          </Badge>
                        </td>
                      );
                    })}
                  </tr>
                  <tr className="hover:bg-accent/50">
                    <td className="p-3 font-medium">vs Moyenne</td>
                    {selectedForComparison.map((site, idx) => {
                      const diff = ((site.total_mwh || 0) / avgConsumption - 1) * 100;
                      const isAbove = diff > 0;
                      return (
                        <td key={idx} className="text-center p-3">
                          <span className={isAbove ? 'text-[hsl(var(--ambre))]' : 'text-[hsl(var(--emeraude))]'}>
                            {isAbove ? '+' : ''}{diff.toFixed(0)}%
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Visual Comparison Chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Comparaison visuelle</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={selectedForComparison.map(site => ({
                        name: site.name.length > 12 ? site.name.substring(0, 12) + '...' : site.name,
                        consumption: site.total_mwh || 0
                      }))}
                      margin={{ top: 5, right: 20, left: 10, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="name" 
                        angle={-45}
                        textAnchor="end"
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis 
                        label={{ value: 'MWh', angle: -90, position: 'insideLeft' }}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip />
                      <Bar dataKey="consumption" fill="#397DFF" radius={[4, 4, 0, 0]} />
                      <ReferenceLine y={avgConsumption} stroke="#ef4444" strokeDasharray="5 5" label="Moyenne" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* CSV Import Modal */}
      <CsvImportModal
        open={showCsvImportModal}
        onOpenChange={setShowCsvImportModal}
        csvImportType={csvImportType}
        csvPreview={csvPreview}
        csvColumnChoice={csvColumnChoice}
        csvCorrections={csvCorrections}
        csvSourceUnit={csvSourceUnit}
        uploadingCsv={uploadingCsv}
        onSitesCsvImport={handleSitesCsvImport}
        onMonthlyCsvPreview={handleMonthlyCsvPreview}
        onColumnChoiceChange={handleColumnChoiceChange}
        onConfirmMonthlyCsvImport={handleConfirmMonthlyCsvImport}
        onCancelCsvPreview={handleCancelCsvPreview}
        onSetCsvSourceUnit={setCsvSourceUnit}
        sitesFileInputRef={sitesFileInputRef}
        monthlyFileInputRef={monthlyFileInputRef}
      />
      
      {/* Contact Info Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Coordonnées du contact
            </DialogTitle>
            <DialogDescription>
              Ces informations apparaîtront sur la dernière page du PDF exporté.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                Nom complet
              </label>
              <Input
                placeholder="Jean Dupont"
                value={contactInfo.name}
                onChange={(e) => setContactInfo(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Fonction / Titre</label>
              <Input
                placeholder="Energy Manager"
                value={contactInfo.title}
                onChange={(e) => setContactInfo(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Building className="w-4 h-4 text-muted-foreground" />
                Agence / Société
              </label>
              <Input
                placeholder="Place des Energies - Lyon"
                value={contactInfo.agency}
                onChange={(e) => setContactInfo(prev => ({ ...prev, agency: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Phone className="w-4 h-4 text-muted-foreground" />
                Téléphone
              </label>
              <Input
                placeholder="06 12 34 56 78"
                value={contactInfo.phone}
                onChange={(e) => setContactInfo(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                Email
              </label>
              <Input
                type="email"
                placeholder="contact@placedesenergies.com"
                value={contactInfo.email}
                onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                Adresse
              </label>
              <Input
                placeholder="123 rue de l'Énergie, 69000 Lyon"
                value={contactInfo.address}
                onChange={(e) => setContactInfo(prev => ({ ...prev, address: e.target.value }))}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowContactModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveContactInfo} disabled={savingContact}>
              {savingContact ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default MultisitesReportViewPage;